import {
  UserFlowInitData,
  UserFlowSafeMethods,
} from '../models/userflow.interface';
import { Observable } from 'rxjs';
import { type Attributes } from 'userflow.js';

/**
 * Service implementation for managing UserFlow functionality.
 *
 * This service provides a wrapper around the UserFlow SDK, handling initialization,
 * user identification, and channel grouping with proper error handling and logging.
 *
 * @example Basic Usage
 * ```typescript
 * // Wait until is ready
 * userFlowService.isReady().then((isReady) => {
 *   if (isReady) {
 *     userFlowService.userflow.start('onboarding-flow-id');
 *   } else {
 *     console.log('UserFlow initialization failed');
 *   }
 * });
 *
 * ```
 *
 * @example Error Handling
 * ```typescript
 * // Handle both success and failure cases
 * userFlowService.isReady().then((isReady) => {
 *   if (isReady) {
 *     // UserFlow initialized successfully
 *     userFlowService.userflow.start('onboarding-flow-id');
 *   } else {
 *     // UserFlow initialization failed
 *     console.error('UserFlow could not be initialized');
 *     // Handle fallback behavior, show error message, etc.
 *   }
 * });
 *
 * // Using async/await
 * const isReady = await userFlowService.isReady();
 * if (isReady) {
 *   userFlowService.userflow.start('onboarding-flow-id');
 * } else {
 *   console.error('UserFlow initialization failed');
 * }
 * ```
 *
 * @example Define Custom hook for React to extract UserFlowService from dependency tree
 * ```typescript
 * import {
 *   TOKENS as MASTERDATA_TOKENS,
 *   UserFlowService,
 * } from '@trustedshops/tswp-core-masterdata';
 *
 * const useUserFlowService = () => {
 *   const service = useRef<UserFlowService | null>(null);
 *   const [isReady, setIsReady] = useState(false);
 *
 *   try {
 *     const pluginContext = useContext(PluginContext);
 *
 *     service.current = pluginContext.serviceProvider.get<UserFlowService>(
 *       MASTERDATA_TOKENS.UserFlowService
 *     );
 *   } catch (error) {
 *     service.current = null;
 *   }
 *
 *   // Wait for service to be ready
 *   useEffect(() => {
 *       if (service.current && !isReady) {
 *         service.current.isReady().then((isReady) => setIsReady(isReady));
 *       }
 *   }, [service.current]);
 *
 *   return {
 *      userFlowService: service.current,
 *      isUserFlowReady: isReady
 *   };
 * }
 * ```
 *
 * @example React Usage with Static Ready Check
 * ```tsx
 * import React, { useState, useEffect } from 'react';
 * import { useUserFlowService } from './hooks.useUserFlowService';
 *
 * const WithUserFlowFeaturesComponent: React.FC = () => {
 *   const { userFlowService, isUserFlowReady } = useUserFlowService() // Should be extracted via custom hook from dependency injection;
 *
 *   // Automatically start onboarding tour when service is ready
 *   useEffect(() => {
 *      if (isUserFlowReady) {
 *         userFlowService.userflow.start('onboarding-flow-id');
 *      }
 *   }, [isUserFlowReady])
 *
 *   // Alternative approach - direct ready check
 *   useEffect(() => {
 *      userFlowService.isReady().then((isReady) => {
 *        if (isReady) {
 *          userFlowService.userflow.start('onboarding-flow-id');
 *        }
 *      });
 *   }, [])
 *
 *   // Conditionally start onboarding tour
 *   const handleStartFlow = () => {
 *     if (isUserFlowReady) {
 *       userFlowService.userflow.start('onboarding-flow-id');
 *     }
 *   };
 *
 *   // If needed, prevent rendering until service is ready
 *   if (!isUserFlowReady) {
 *     return <div>Initializing UserFlow...</div>;
 *   }
 *
 *   return (
 *     <div>
 *       <p>UserFlow Status: {isUserFlowReady ? 'Ready' : 'Not Ready'}</p>
 *       <button
 *         onClick={handleStartFlow}
 *         disabled={!isUserFlowReady}
 *       >
 *         Start Flow
 *       </button>
 *     </div>
 *   );
 * };
 *
 * export default WithUserFlowFeaturesComponent;
 * ```
 *
 * @example Observable Usage
 * ```typescript
 * import { filter, take } from 'rxjs/operators';
 *
 * // Subscribe to initialization state changes
 * userFlowService.isReady$.subscribe((isReady) => {
 *   if (isReady) {
 *     console.log('UserFlow is ready');
 *     userFlowService.userflow.start('onboarding-flow-id');
 *   } else {
 *     console.log('UserFlow initialization failed');
 *   }
 * });
 *
 * // Using with RxJS operators
 * userFlowService.isReady$.pipe(
 *   filter(isReady => isReady === true),
 *   take(1)
 * ).subscribe(() => {
 *   userFlowService.userflow.start('onboarding-flow-id');
 * });
 * ```
 *
 * @example Angular Usage with RxJS Reactive Approach
 * ```typescript
 * import { Component } from '@angular/core';
 * import {
 *   TOKENS as MASTERDATA_TOKENS,
 *   UserFlowService,
 * } from '@trustedshops/tswp-core-masterdata';
 *
 * @Component({
 *   selector: 'app-userflow',
 *   template: `
 *     <div>
 *       <p>UserFlow Status: {{ getStatusText(userFlowService.isReady$ | async) }}</p>
 *       <button
 *         (click)="startFlow()"
 *         [disabled]="!(userFlowService.isReady$ | async)"
 *       >
 *         Start Onboarding Flow
 *       </button>
 *       <div *ngIf="(userFlowService.isReady$ | async) === false">UserFlow initialization failed</div>
 *     </div>
 *   `
 * })
 * export class WithUserFlowFeaturesComponent {
 *   constructor(@Inject(MASTERDATA_TOKENS.UserFlowService) private readonly userFlowService: UserFlowService) {}
 *
 *   startFlow(): void {
 *     this.userFlowService.userflow.start('onboarding-flow-id');
 *   }
 *
 *   getStatusText(isReady: boolean | null): string {
 *     if (isReady === null) return 'Initializing...';
 *     return isReady ? 'Ready' : 'Failed';
 *   }
 * }
 * ```
 */
export interface UserFlowService {
  /**
   * Gets an Observable stream of the initialization status.
   *
   * @returns Observable that emits true when service is ready, false when failed
   */
  get isReady$(): Observable<boolean>;

  /**
   * Gets the UserFlow SDK instance with safe methods.
   * Only use this after confirming the service is ready via `isReady`.
   *
   * @returns UserFlow SDK instance with safe method access
   */
  get userflow(): UserFlowSafeMethods;

  /**
   * Initializes the UserFlow service with API key, user identification, and channel grouping.
   * Must be initialized in the root app.
   *
   * This method:
   * 1. Checks if already initialized to prevent duplicate initialization
   * 2. Initializes UserFlow with the provided API key
   * 3. Identifies the user with their attributes
   * 4. Groups the user into the specified channel
   * 5. Updates the initialization state
   *
   * @template User - Type extending Attributes for user data
   * @template Channel - Type extending Attributes for channel data
   * @param config - Configuration object containing API key, user, and channel data
   * @param config.apiKey - UserFlow API key for authentication
   * @param config.user - User object containing identityRef and additional attributes
   * @param config.channel - Channel object containing channelId and additional attributes
   * @returns Promise that resolves when initialization is complete
   *
   * @throws Will log errors but not throw exceptions - check `isReady` to verify success (returns true/false)
   */
  init: <User extends Attributes, Channel extends Attributes>(userFlowInitData: UserFlowInitData<User, Channel>) => Promise<void>;

  /**
   * Gets a Promise of the initialization status.
   *
   * @returns Promise that resolves to true when service is ready, false when failed
   */
  isReady(): Promise<boolean>;
}
